import { DefaultButton, Dialog, DialogFooter, MessageBar, MessageBarType, PrimaryButton, Spinner, SpinnerSize, Stack } from "@fluentui/react"
import { useEffect, useState } from "react"
import { safeFormatDateValue } from "../lib/helper"
import { makeCRMApiCall } from "../services/apiService"
import { IAccount } from "../types/IAccount"
import { UploadCensusDialog } from "./uploadCensusDialog"

interface IRequoteDialogProps {
	group: IAccount
	onDismiss: () => void
	onSave: (date: string) => void
}

const getNextEffectiveDate = (dateStr?: string): string => {
	const today = new Date()
	const ref = dateStr ? new Date(dateStr) : new Date()

	const target = new Date(Date.UTC(today.getFullYear(), ref.getUTCMonth(), 1))

	if (target.getTime() <= today.getTime()) {
		target.setUTCFullYear(target.getUTCFullYear() + 1)
	}

	return target.toISOString().substring(0, 10)
}

export function RequoteDialog({ group, onDismiss, onSave }: IRequoteDialogProps) {
	const [error, setError] = useState<string | undefined>()
	const [showUploadCensusDialog, setShowUploadCensusDialog] = useState<IAccount>(undefined)
	const [workflowStep, setWorkflowStep] = useState<"set-date" | "upload-census" | "create-opportunity">("set-date")
	const [saving, setSaving] = useState(false)
	const [isPortalReady, setIsPortalReady] = useState(false)

	const effectiveDate = getNextEffectiveDate(group.cw_coveragestartdate)

	const requoteSteps = [
		{ key: "set-date", label: "Confirm Date" },
		{ key: "upload-census", label: "Census" },
		{ key: "create-opportunity", label: "Finish" }
	]
	const currentStepIndex = requoteSteps.findIndex(s => s.key === workflowStep)

	useEffect(() => {
		// still doesn't work sometimes -- have to wait
		const checkReady = () => {
			if ((window.top as any).shell && (window.top as any).webapi) {
				setTimeout(() => setIsPortalReady(true), 500)
			} else {
				setTimeout(checkReady, 200)
			}
		}
		checkReady()
	}, [])

	useEffect(() => {
		if (workflowStep === "create-opportunity" && !saving && !error) {
			createNewOpportunity()
		}
	}, [workflowStep])

	const updateEffectiveDate = async () => {
		if (!effectiveDate) return

		try {
			setSaving(true)
			setError(undefined)

			const body = { cw_coveragestartdate: effectiveDate }

			await makeCRMApiCall(`accounts(${group.accountid})`, "PATCH", JSON.stringify(body), undefined)
			setWorkflowStep("upload-census")
		} catch (err: any) {
			setError(err.message)
		} finally {
			setSaving(false)
		}
	}

	const createNewOpportunity = async () => {
		try {
			setError(undefined)
			setSaving(true)

			const body = {
				"parentaccountid@odata.bind": `/accounts(${group.accountid})`,
				cradf_effectivedate: effectiveDate,
				name: `Initial - ${group.name} - ${safeFormatDateValue(effectiveDate, "MM/dd/yyyy")}`,
				cw_isrenewal: false,
				cw_opportunitytype: 121640000
			}

			await makeCRMApiCall("opportunities", "POST", JSON.stringify(body), undefined)
		} catch (err: any) {
			setError(err.message)
		} finally {
			setSaving(false)
		}
	}

	return (
		<>
			<Dialog hidden={false} onDismiss={onDismiss} dialogContentProps={{ title: "Request New Quote", subText: `Group: ${group.name}`, styles: { inner: { paddingTop: 0 } } }}>
				<Stack tokens={{ childrenGap: 10 }} styles={{ root: { marginBottom: 25, marginTop: 10 } }}>
					<Stack horizontal horizontalAlign="center" verticalAlign="center">
						{requoteSteps.map((_, index) => (
							<Stack key={index} horizontal verticalAlign="center">
								<div
									style={{
										width: 28,
										height: 28,
										borderRadius: "50%",
										background: index <= currentStepIndex ? "#0078d4" : "#edebe9",
										color: "white",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										fontSize: 12,
										fontWeight: "bold",
										flexShrink: 0
									}}>
									{index + 1}
								</div>

								{index < requoteSteps.length - 1 && (
									<div
										style={{
											width: 70,
											height: 2,
											background: index < currentStepIndex ? "#0078d4" : "#edebe9"
										}}
									/>
								)}
							</Stack>
						))}
					</Stack>

					<Stack horizontal horizontalAlign="center" tokens={{ childrenGap: 28 }}>
						{requoteSteps.map((step, index) => (
							<span
								key={index}
								style={{
									width: 70,
									textAlign: "center",
									fontSize: 11,
									color: index === currentStepIndex ? "#000" : "#a19f9d",
									fontWeight: index === currentStepIndex ? "bold" : "normal"
								}}>
								{step.label}
							</span>
						))}
					</Stack>
				</Stack>

				{error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
				{workflowStep === "set-date" && (
					<Stack tokens={{ childrenGap: 15 }}>
						{/* <Label required>New Effective Date</Label>
						<Calendar
							dateRangeType={DateRangeType.Month}
							showGoToToday={false}
							highlightSelectedMonth
							minDate={minDate}
							isDayPickerVisible={false}
							onSelectDate={date => {
								const firstDay = new Date(date.getFullYear(), date.getMonth(), 1)
								setEffectiveDate(firstDay?.toISOString().substring(0, 10))
							}}
							value={effectiveDate ? new Date(effectiveDate.replace(/.$/, "2")) : undefined}
							strings={defaultCalendarStrings}
						/> */}
						<MessageBar>
							The effective date for this requote has been automatically set to <strong>{safeFormatDateValue(effectiveDate, "MM/dd/yyyy")}</strong>
						</MessageBar>

						<DialogFooter>
							<PrimaryButton text="Confirm & Continue" disabled={saving || !isPortalReady} onClick={updateEffectiveDate} />
							<DefaultButton text="Cancel" onClick={onDismiss} />
						</DialogFooter>
					</Stack>
				)}
				{workflowStep === "upload-census" && (
					<Stack tokens={{ childrenGap: 10 }}>
						<p>
							A new census is required for the <strong>{safeFormatDateValue(effectiveDate, "MM/yyyy")}</strong> quote request.
						</p>
						<DialogFooter>
							<PrimaryButton
								text="Yes, New Census"
								onClick={() => {
									setShowUploadCensusDialog(group)
								}}
								disabled={!isPortalReady}
							/>
							{/* <DefaultButton text="No, Keep Current" onClick={() => setWorkflowStep("create-opportunity")} /> */}
							<DefaultButton text="Cancel" onClick={onDismiss} />
						</DialogFooter>
					</Stack>
				)}
				{workflowStep === "create-opportunity" && (
					<Stack>
						{saving ? (
							<>
								<Spinner size={SpinnerSize.large} label="Processing Quote Request..." />
							</>
						) : error ? (
							<PrimaryButton text="Close" onClick={onDismiss} />
						) : (
							<>
								<Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
									<span>Quote Request Received, somebody at ACMG will be in touch shortly</span>
								</Stack>
								<DialogFooter>
									<PrimaryButton
										text="Finish"
										onClick={() => {
											onSave(effectiveDate!)
											onDismiss()
										}}
									/>
								</DialogFooter>
							</>
						)}
					</Stack>
				)}
			</Dialog>
			{showUploadCensusDialog && (
				<UploadCensusDialog
					closePanel={refreshItems => {
						setShowUploadCensusDialog(undefined)
						if (refreshItems) {
							setWorkflowStep("create-opportunity")
						}
					}}
					group={showUploadCensusDialog}
					hideEnrollmentLink
				/>
			)}
		</>
	)
}
